import axios from 'axios'
import { ELEMENT_IDS } from 'fitify-ui-landing/src/@types/constants'
import Container from 'fitify-ui-landing/src/components/Container/Container'
import Section from 'fitify-ui-landing/src/components/Section/Section'
import { Text } from 'fitify-ui-landing/src/components/Text'
import { EMAIL_REGEXP } from 'fitify-utils/src/validations'
import { useContext, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ThemeContext } from 'styled-components'

import { ContentRevealAnimation } from '../../animations/ContentRevealAnimation'

import {
  ButtonLoader,
  StyledContact,
  StyledContactButton,
  StyledContactCopy,
  StyledContactEmailSuccessMessage,
  StyledContactErrorMessage,
  StyledContactForm,
  StyledContactInput,
} from './BusinessContact.Styled'

type BusinessContactFormInputs = {
  email: string
  name: string
  country: string
  companyAndJob: string
}

type BusinessContactProps = {
  titleText: string
  subtitleText: string
  emailText: string
  nameText: string
  ctaText: string
  successMessageText: string
  countryText: string
  companyAndJobText: string
  nameErrorText: string
  emailErrorText: string
  countryErrorText: string
  companyAndJobErrorText: string
}

const BusinessContact = ({
  titleText,
  subtitleText,
  emailText,
  nameText,
  ctaText,
  successMessageText,
  countryText,
  companyAndJobText,
  nameErrorText,
  emailErrorText,
  countryErrorText,
  companyAndJobErrorText,
}: BusinessContactProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isSubmitSuccessful },
  } = useForm<BusinessContactFormInputs>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFaded, setIsFaded] = useState<boolean>(false)
  const timer = useRef<NodeJS.Timeout | undefined>(undefined)
  const { t } = useTranslation()

  const theme = useContext(ThemeContext)

  useEffect(() => {
    if (!isSubmitSuccessful) {
      setIsFaded(false)
      return
    }

    if (!isLoading) setIsFaded(true)

    timer.current = setTimeout(() => {
      setIsFaded(false)
    }, 5000)

    return () => {
      clearTimeout(timer.current)
    }
  }, [isSubmitSuccessful, isLoading])

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true)
    try {
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_API_URL}/sendFitifyBusinessRequest`,
        {
          variant: theme?.variant,
          email: data.email,
          name: data.name,
          country: data.country,
          companyAndJob: data.companyAndJob,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (response.status === 200) {
        setValue('email', '')
        setValue('name', '')
        setValue('country', '')
        setValue('companyAndJob', '')
      }

      setIsLoading(false)
    } catch {
      setIsLoading(false)
      setError('email', {
        type: 'server',
        message: t('email_sign_up_invalid_credentials_message'),
      })
    }
  })

  return (
    <Section id={ELEMENT_IDS.contact} style={{ marginBottom: '80px' }}>
      <Container>
        <ContentRevealAnimation>
          <StyledContact>
            <StyledContactCopy>
              <Text as={'h2'} variant={'newsletter-title'} html={titleText} />
              <Text as={'p'} variant={'review-content'} html={subtitleText} />
            </StyledContactCopy>
            <StyledContactForm onSubmit={onSubmit}>
              <StyledContactInput
                type={'text'}
                placeholder={nameText}
                {...register('name', { required: true })}
                disabled={isFaded}
                invalid={!!errors.name}
              />
              {errors.name && (
                <StyledContactErrorMessage>
                  {nameErrorText}
                </StyledContactErrorMessage>
              )}
              <StyledContactInput
                placeholder={emailText}
                {...register('email', {
                  required: true,
                  pattern: EMAIL_REGEXP,
                })}
                disabled={isFaded}
                invalid={!!errors.email}
              />
              {errors.email && (
                <StyledContactErrorMessage>
                  {emailErrorText}
                </StyledContactErrorMessage>
              )}
              <StyledContactInput
                type={'text'}
                placeholder={countryText}
                {...register('country', { required: true })}
                disabled={isFaded}
                invalid={!!errors.country}
              />
              {errors.country && (
                <StyledContactErrorMessage>
                  {countryErrorText}
                </StyledContactErrorMessage>
              )}
              <StyledContactInput
                type={'text'}
                placeholder={companyAndJobText}
                {...register('companyAndJob', { required: true })}
                disabled={isFaded}
                invalid={!!errors.companyAndJob}
              />
              {errors.companyAndJob && (
                <StyledContactErrorMessage>
                  {companyAndJobErrorText}
                </StyledContactErrorMessage>
              )}
              {isLoading ? <ButtonLoader /> : null}
              <StyledContactButton type="submit" disabled={isLoading}>
                {ctaText}
              </StyledContactButton>
              {isFaded ? (
                <StyledContactEmailSuccessMessage isSuccess={isFaded}>
                  {successMessageText}
                </StyledContactEmailSuccessMessage>
              ) : null}
            </StyledContactForm>
          </StyledContact>
        </ContentRevealAnimation>
      </Container>
    </Section>
  )
}

export default BusinessContact
